import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ChevronLeft, Save, Eye, Plus, Trash2, GripVertical, Image as ImageIcon,
  Type, Trophy, Calendar, Users, HelpCircle, Star, Upload, X, Check,
  Layout, Palette, Settings, ArrowUp, ArrowDown, Edit, Copy, Loader2,
  Globe, FileText, Award, Clock, Target, Zap, Building, Mail
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Available section types
const sectionTypes = [
  { id: 'hero', name: 'Hero Section', icon: Layout, description: 'Main banner with title, subtitle, and CTA' },
  { id: 'about', name: 'About', icon: FileText, description: 'Challenge description and details' },
  { id: 'timeline', name: 'Timeline', icon: Calendar, description: 'Event schedule and milestones' },
  { id: 'prizes', name: 'Prizes', icon: Trophy, description: 'Prize pool and rewards' },
  { id: 'sponsors', name: 'Sponsors', icon: Building, description: 'Sponsor logos and tiers' },
  { id: 'faq', name: 'FAQ', icon: HelpCircle, description: 'Frequently asked questions' },
  { id: 'eligibility', name: 'Eligibility', icon: Users, description: 'Who can participate' },
  { id: 'custom', name: 'Custom Block', icon: Type, description: 'Custom content with rich text' },
  { id: 'image_gallery', name: 'Image Gallery', icon: ImageIcon, description: 'Photo gallery section' },
  { id: 'stats', name: 'Statistics', icon: Target, description: 'Key numbers and metrics' },
  { id: 'cta', name: 'Call to Action', icon: Zap, description: 'Registration or action button' },
  { id: 'contact', name: 'Contact', icon: Mail, description: 'Contact information' }
];

// Default section templates
const defaultSections = {
  hero: {
    title: 'Challenge Title',
    subtitle: 'Your tagline goes here',
    backgroundImage: '',
    backgroundColor: '#0a0a0f',
    ctaText: 'Register Now',
    ctaLink: '#register',
    showCountdown: true
  },
  about: {
    title: 'About the Challenge',
    content: 'Describe your challenge here...',
    features: [
      { icon: 'trophy', title: 'Feature 1', description: 'Description' },
      { icon: 'users', title: 'Feature 2', description: 'Description' },
      { icon: 'clock', title: 'Feature 3', description: 'Description' }
    ]
  },
  timeline: {
    title: 'Event Timeline',
    events: [
      { date: '2026-04-01', title: 'Registration Opens', description: 'Start registering' },
      { date: '2026-04-15', title: 'Round 1', description: 'Online assessment' },
      { date: '2026-04-30', title: 'Finals', description: 'Final round' }
    ]
  },
  prizes: {
    title: 'Prizes & Rewards',
    totalPool: '₹5,00,000',
    prizes: [
      { position: '1st', amount: '₹2,00,000', perks: ['Trophy', 'Certificate', 'Internship'] },
      { position: '2nd', amount: '₹1,00,000', perks: ['Trophy', 'Certificate'] },
      { position: '3rd', amount: '₹50,000', perks: ['Trophy', 'Certificate'] }
    ]
  },
  sponsors: {
    title: 'Our Sponsors',
    tiers: [
      { name: 'Title Sponsor', logos: [] },
      { name: 'Gold Sponsors', logos: [] },
      { name: 'Silver Sponsors', logos: [] }
    ]
  },
  faq: {
    title: 'Frequently Asked Questions',
    questions: [
      { question: 'Who can participate?', answer: 'Anyone can participate!' },
      { question: 'Is there a fee?', answer: 'Registration is free.' },
      { question: 'What is the format?', answer: 'Online assessment followed by finals.' }
    ]
  },
  eligibility: {
    title: 'Eligibility Criteria',
    criteria: [
      'Must be enrolled in a recognized institution',
      'Age 18-30 years',
      'Valid email and phone number'
    ]
  },
  custom: {
    title: 'Custom Section',
    content: '<p>Add your custom content here...</p>',
    backgroundColor: 'transparent'
  },
  image_gallery: {
    title: 'Gallery',
    images: []
  },
  stats: {
    title: 'Challenge Stats',
    stats: [
      { value: '10,000+', label: 'Participants' },
      { value: '₹5L', label: 'Prize Pool' },
      { value: '100+', label: 'Colleges' },
      { value: '3', label: 'Rounds' }
    ]
  },
  cta: {
    title: 'Ready to Compete?',
    subtitle: 'Register now and showcase your skills',
    buttonText: 'Register Now',
    buttonLink: '#register',
    backgroundColor: 'gradient'
  },
  contact: {
    title: 'Contact Us',
    email: 'contact@example.com',
    phone: '+91 9876543210',
    address: 'Your address here'
  }
};

const EnterpriseLandingPageBuilder = () => {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const [token] = useState(localStorage.getItem('company_token'));
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [showAddSection, setShowAddSection] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Theme settings
  const [theme, setTheme] = useState({
    primaryColor: '#8b5cf6',
    secondaryColor: '#06b6d4',
    backgroundColor: '#0a0a0f',
    textColor: '#ffffff',
    fontFamily: 'Inter'
  });

  useEffect(() => {
    if (!token) {
      navigate('/company/auth');
      return;
    }
    fetchChallenge();
  }, [token, challengeId]);

  const fetchChallenge = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/company/enterprise/challenges/${challengeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChallenge(res.data.challenge);
      
      // Load existing landing page config or use defaults
      const landingConfig = res.data.challenge.landing_page_config || {};
      setSections(landingConfig.sections || [
        { id: 'hero-1', type: 'hero', ...defaultSections.hero },
        { id: 'about-1', type: 'about', ...defaultSections.about },
        { id: 'prizes-1', type: 'prizes', ...defaultSections.prizes }
      ]);
      setTheme(landingConfig.theme || theme);
    } catch (err) {
      toast.error('Failed to load challenge');
      navigate('/company/enterprise');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(
        `${API}/api/company/enterprise/challenges/${challengeId}`,
        {
          landing_page_config: {
            sections,
            theme,
            lastUpdated: new Date().toISOString()
          }
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Landing page saved successfully!');
    } catch (err) {
      toast.error('Failed to save landing page');
    } finally {
      setSaving(false);
    }
  };

  const addSection = (type) => {
    const newSection = {
      id: `${type}-${Date.now()}`,
      type,
      ...defaultSections[type]
    };
    setSections([...sections, newSection]);
    setSelectedSection(newSection.id);
    setShowAddSection(false);
    toast.success(`${sectionTypes.find(s => s.id === type)?.name} added`);
  };

  const removeSection = (sectionId) => {
    setSections(sections.filter(s => s.id !== sectionId));
    if (selectedSection === sectionId) {
      setSelectedSection(null);
    }
    toast.success('Section removed');
  };

  const moveSection = (index, direction) => {
    const newSections = [...sections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sections.length) return;
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    setSections(newSections);
  };

  const updateSection = (sectionId, updates) => {
    setSections(sections.map(s => 
      s.id === sectionId ? { ...s, ...updates } : s
    ));
  };

  const handleImageUpload = async (e, sectionId, field) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    try {
      setUploadingImage(true);
      
      // Convert to base64 for storage (in production, upload to cloud storage)
      const reader = new FileReader();
      reader.onload = () => {
        updateSection(sectionId, { [field]: reader.result });
        toast.success('Image uploaded');
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error('Failed to upload image');
      setUploadingImage(false);
    }
  };

  const duplicateSection = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;
    
    const newSection = {
      ...section,
      id: `${section.type}-${Date.now()}`
    };
    const index = sections.findIndex(s => s.id === sectionId);
    const newSections = [...sections];
    newSections.splice(index + 1, 0, newSection);
    setSections(newSections);
    toast.success('Section duplicated');
  };

  const currentSection = sections.find(s => s.id === selectedSection);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <Loader2 className="animate-spin text-purple-500" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex flex-col">
      {/* Header */}
      <header className="bg-[#12121a] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-full mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/company/enterprise/challenges/${challengeId}`} className="text-gray-400 hover:text-white">
                <ChevronLeft size={24} />
              </Link>
              <div>
                <h1 className="text-lg font-bold text-white">Landing Page Builder</h1>
                <p className="text-sm text-gray-400">{challenge?.title}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setPreviewMode(!previewMode)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                  previewMode 
                    ? 'bg-purple-500/20 text-purple-400' 
                    : 'bg-white/5 text-gray-400 hover:text-white'
                }`}
              >
                <Eye size={18} />
                {previewMode ? 'Edit Mode' : 'Preview'}
              </button>
              
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-cyan-500 text-white rounded-lg flex items-center gap-2"
              >
                {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                Save
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 flex">
        {/* Sections List (Left Sidebar) */}
        {!previewMode && (
          <aside className="w-72 bg-[#12121a] border-r border-white/10 p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-semibold">Sections</h2>
              <button
                onClick={() => setShowAddSection(true)}
                className="p-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30"
              >
                <Plus size={18} />
              </button>
            </div>
            
            <div className="space-y-2">
              {sections.map((section, idx) => {
                const sectionType = sectionTypes.find(t => t.id === section.type);
                const Icon = sectionType?.icon || Type;
                
                return (
                  <div
                    key={section.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedSection === section.id
                        ? 'border-purple-500 bg-purple-500/10'
                        : 'border-white/10 bg-white/5 hover:border-white/20'
                    }`}
                    onClick={() => setSelectedSection(section.id)}
                  >
                    <div className="flex items-center gap-3">
                      <GripVertical className="text-gray-600 cursor-move" size={16} />
                      <Icon size={16} className="text-gray-400" />
                      <span className="text-white text-sm flex-1 truncate">
                        {section.title || sectionType?.name}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-1 mt-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); moveSection(idx, 'up'); }}
                        disabled={idx === 0}
                        className="p-1 text-gray-500 hover:text-white disabled:opacity-30"
                      >
                        <ArrowUp size={14} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); moveSection(idx, 'down'); }}
                        disabled={idx === sections.length - 1}
                        className="p-1 text-gray-500 hover:text-white disabled:opacity-30"
                      >
                        <ArrowDown size={14} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); duplicateSection(section.id); }}
                        className="p-1 text-gray-500 hover:text-white"
                      >
                        <Copy size={14} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); removeSection(section.id); }}
                        className="p-1 text-gray-500 hover:text-red-400"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Theme Settings */}
            <div className="mt-6 p-4 bg-white/5 rounded-xl">
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <Palette size={16} />
                Theme
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Primary Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={theme.primaryColor}
                      onChange={(e) => setTheme({ ...theme, primaryColor: e.target.value })}
                      className="w-8 h-8 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={theme.primaryColor}
                      onChange={(e) => setTheme({ ...theme, primaryColor: e.target.value })}
                      className="flex-1 px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Secondary Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={theme.secondaryColor}
                      onChange={(e) => setTheme({ ...theme, secondaryColor: e.target.value })}
                      className="w-8 h-8 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={theme.secondaryColor}
                      onChange={(e) => setTheme({ ...theme, secondaryColor: e.target.value })}
                      className="flex-1 px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </aside>
        )}

        {/* Preview/Edit Panel (Center) */}
        <main className={`flex-1 overflow-y-auto ${previewMode ? '' : 'p-6'}`}>
          {previewMode ? (
            <LandingPagePreview sections={sections} theme={theme} challenge={challenge} />
          ) : (
            <div className="max-w-4xl mx-auto">
              {sections.length === 0 ? (
                <div className="text-center py-20 bg-white/5 border border-white/10 rounded-2xl border-dashed">
                  <Layout size={64} className="mx-auto mb-4 text-gray-600" />
                  <h3 className="text-xl font-semibold text-white mb-2">No Sections Yet</h3>
                  <p className="text-gray-400 mb-6">Add sections to build your landing page</p>
                  <button
                    onClick={() => setShowAddSection(true)}
                    className="px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 text-white rounded-lg"
                  >
                    Add First Section
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {sections.map((section, idx) => (
                    <SectionPreviewCard
                      key={section.id}
                      section={section}
                      isSelected={selectedSection === section.id}
                      onClick={() => setSelectedSection(section.id)}
                      theme={theme}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </main>

        {/* Section Editor (Right Sidebar) */}
        {!previewMode && currentSection && (
          <aside className="w-96 bg-[#12121a] border-l border-white/10 p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-semibold">Edit Section</h2>
              <button
                onClick={() => setSelectedSection(null)}
                className="text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>
            
            <SectionEditor
              section={currentSection}
              onUpdate={(updates) => updateSection(currentSection.id, updates)}
              onImageUpload={(e, field) => handleImageUpload(e, currentSection.id, field)}
              uploadingImage={uploadingImage}
            />
          </aside>
        )}
      </div>

      {/* Add Section Modal */}
      {showAddSection && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">Add Section</h2>
              <button onClick={() => setShowAddSection(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-2 gap-3">
                {sectionTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => addSection(type.id)}
                    className="p-4 bg-white/5 border border-white/10 rounded-xl hover:border-purple-500/50 hover:bg-purple-500/10 transition-all text-left"
                  >
                    <type.icon className="text-purple-400 mb-2" size={24} />
                    <h3 className="text-white font-medium">{type.name}</h3>
                    <p className="text-gray-500 text-sm">{type.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Section Editor Component
const SectionEditor = ({ section, onUpdate, onImageUpload, uploadingImage }) => {
  switch (section.type) {
    case 'hero':
      return (
        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-400 block mb-1">Title</label>
            <input
              type="text"
              value={section.title || ''}
              onChange={(e) => onUpdate({ title: e.target.value })}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 block mb-1">Subtitle</label>
            <input
              type="text"
              value={section.subtitle || ''}
              onChange={(e) => onUpdate({ subtitle: e.target.value })}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 block mb-1">Background Image</label>
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => onImageUpload(e, 'backgroundImage')}
                className="hidden"
                id="hero-bg-upload"
              />
              <label
                htmlFor="hero-bg-upload"
                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-gray-400 cursor-pointer hover:border-purple-500/50 flex items-center gap-2"
              >
                {uploadingImage ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
                {section.backgroundImage ? 'Change Image' : 'Upload Image'}
              </label>
            </div>
            {section.backgroundImage && (
              <img src={section.backgroundImage} alt="Preview" className="mt-2 w-full h-20 object-cover rounded-lg" />
            )}
          </div>
          <div>
            <label className="text-sm text-gray-400 block mb-1">CTA Button Text</label>
            <input
              type="text"
              value={section.ctaText || ''}
              onChange={(e) => onUpdate({ ctaText: e.target.value })}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={section.showCountdown || false}
              onChange={(e) => onUpdate({ showCountdown: e.target.checked })}
              className="w-4 h-4 rounded"
            />
            <span className="text-gray-300 text-sm">Show Countdown Timer</span>
          </label>
        </div>
      );

    case 'about':
      return (
        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-400 block mb-1">Title</label>
            <input
              type="text"
              value={section.title || ''}
              onChange={(e) => onUpdate({ title: e.target.value })}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 block mb-1">Content</label>
            <textarea
              value={section.content || ''}
              onChange={(e) => onUpdate({ content: e.target.value })}
              rows={6}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white resize-none"
            />
          </div>
        </div>
      );

    case 'prizes':
      return (
        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-400 block mb-1">Section Title</label>
            <input
              type="text"
              value={section.title || ''}
              onChange={(e) => onUpdate({ title: e.target.value })}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 block mb-1">Total Prize Pool</label>
            <input
              type="text"
              value={section.totalPool || ''}
              onChange={(e) => onUpdate({ totalPool: e.target.value })}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 block mb-2">Prizes</label>
            {section.prizes?.map((prize, idx) => (
              <div key={idx} className="p-3 bg-white/5 rounded-lg mb-2 space-y-2">
                <input
                  type="text"
                  value={prize.position || ''}
                  onChange={(e) => {
                    const newPrizes = [...section.prizes];
                    newPrizes[idx] = { ...prize, position: e.target.value };
                    onUpdate({ prizes: newPrizes });
                  }}
                  placeholder="Position (e.g., 1st)"
                  className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-sm"
                />
                <input
                  type="text"
                  value={prize.amount || ''}
                  onChange={(e) => {
                    const newPrizes = [...section.prizes];
                    newPrizes[idx] = { ...prize, amount: e.target.value };
                    onUpdate({ prizes: newPrizes });
                  }}
                  placeholder="Amount"
                  className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-sm"
                />
              </div>
            ))}
            <button
              onClick={() => onUpdate({ prizes: [...(section.prizes || []), { position: '', amount: '', perks: [] }] })}
              className="text-purple-400 text-sm hover:text-purple-300"
            >
              + Add Prize
            </button>
          </div>
        </div>
      );

    case 'faq':
      return (
        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-400 block mb-1">Section Title</label>
            <input
              type="text"
              value={section.title || ''}
              onChange={(e) => onUpdate({ title: e.target.value })}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 block mb-2">Questions</label>
            {section.questions?.map((faq, idx) => (
              <div key={idx} className="p-3 bg-white/5 rounded-lg mb-2 space-y-2">
                <input
                  type="text"
                  value={faq.question || ''}
                  onChange={(e) => {
                    const newQuestions = [...section.questions];
                    newQuestions[idx] = { ...faq, question: e.target.value };
                    onUpdate({ questions: newQuestions });
                  }}
                  placeholder="Question"
                  className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-sm"
                />
                <textarea
                  value={faq.answer || ''}
                  onChange={(e) => {
                    const newQuestions = [...section.questions];
                    newQuestions[idx] = { ...faq, answer: e.target.value };
                    onUpdate({ questions: newQuestions });
                  }}
                  placeholder="Answer"
                  rows={2}
                  className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-sm resize-none"
                />
                <button
                  onClick={() => {
                    const newQuestions = section.questions.filter((_, i) => i !== idx);
                    onUpdate({ questions: newQuestions });
                  }}
                  className="text-red-400 text-xs hover:text-red-300"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              onClick={() => onUpdate({ questions: [...(section.questions || []), { question: '', answer: '' }] })}
              className="text-purple-400 text-sm hover:text-purple-300"
            >
              + Add Question
            </button>
          </div>
        </div>
      );

    case 'custom':
      return (
        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-400 block mb-1">Title</label>
            <input
              type="text"
              value={section.title || ''}
              onChange={(e) => onUpdate({ title: e.target.value })}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 block mb-1">Content (HTML supported)</label>
            <textarea
              value={section.content || ''}
              onChange={(e) => onUpdate({ content: e.target.value })}
              rows={10}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white resize-none font-mono text-sm"
            />
          </div>
        </div>
      );

    default:
      return (
        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-400 block mb-1">Title</label>
            <input
              type="text"
              value={section.title || ''}
              onChange={(e) => onUpdate({ title: e.target.value })}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
            />
          </div>
          <p className="text-gray-500 text-sm">More options coming soon for this section type.</p>
        </div>
      );
  }
};

// Section Preview Card
const SectionPreviewCard = ({ section, isSelected, onClick, theme }) => {
  const sectionType = sectionTypes.find(t => t.id === section.type);
  const Icon = sectionType?.icon || Type;
  
  return (
    <div
      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
        isSelected 
          ? 'border-purple-500 bg-purple-500/10' 
          : 'border-white/10 bg-white/5 hover:border-white/20'
      }`}
      onClick={onClick}
    >
      <div className="flex items-center gap-3 mb-3">
        <Icon size={20} className="text-purple-400" />
        <span className="text-white font-medium">{section.title || sectionType?.name}</span>
        <span className="text-gray-500 text-xs ml-auto">{sectionType?.name}</span>
      </div>
      
      {/* Mini Preview */}
      <div className="bg-black/30 rounded-lg p-3 text-sm">
        {section.type === 'hero' && (
          <div className="text-center">
            <div className="text-white font-bold">{section.title}</div>
            <div className="text-gray-400 text-xs">{section.subtitle}</div>
          </div>
        )}
        {section.type === 'about' && (
          <div className="text-gray-300 text-xs line-clamp-2">{section.content}</div>
        )}
        {section.type === 'prizes' && (
          <div className="text-cyan-400 text-center font-bold">{section.totalPool}</div>
        )}
        {section.type === 'faq' && (
          <div className="text-gray-400 text-xs">{section.questions?.length || 0} questions</div>
        )}
        {section.type === 'custom' && (
          <div className="text-gray-300 text-xs line-clamp-2" dangerouslySetInnerHTML={{ __html: section.content }} />
        )}
      </div>
    </div>
  );
};

// Landing Page Preview Component
const LandingPagePreview = ({ sections, theme, challenge }) => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.backgroundColor, color: theme.textColor }}>
      {sections.map((section) => {
        switch (section.type) {
          case 'hero':
            return (
              <div 
                key={section.id}
                className="min-h-[60vh] flex items-center justify-center relative"
                style={{ 
                  backgroundImage: section.backgroundImage ? `url(${section.backgroundImage})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                <div className="absolute inset-0 bg-black/60" />
                <div className="relative text-center px-4">
                  <h1 className="text-5xl md:text-7xl font-bold mb-4" style={{ color: theme.primaryColor }}>
                    {section.title}
                  </h1>
                  <p className="text-xl md:text-2xl text-gray-300 mb-8">{section.subtitle}</p>
                  <button 
                    className="px-8 py-4 rounded-xl font-semibold text-white text-lg"
                    style={{ background: `linear-gradient(to right, ${theme.primaryColor}, ${theme.secondaryColor})` }}
                  >
                    {section.ctaText}
                  </button>
                </div>
              </div>
            );
          
          case 'about':
            return (
              <div key={section.id} className="py-20 px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">{section.title}</h2>
                  <p className="text-gray-300 text-lg leading-relaxed whitespace-pre-line">{section.content}</p>
                </div>
              </div>
            );
          
          case 'prizes':
            return (
              <div key={section.id} className="py-20 px-4" style={{ background: 'rgba(255,255,255,0.02)' }}>
                <div className="max-w-4xl mx-auto text-center">
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">{section.title}</h2>
                  <div className="text-4xl md:text-6xl font-bold mb-12" style={{ color: theme.secondaryColor }}>
                    {section.totalPool}
                  </div>
                  <div className="grid md:grid-cols-3 gap-6">
                    {section.prizes?.map((prize, idx) => (
                      <div key={idx} className="p-6 rounded-2xl" style={{ background: 'rgba(255,255,255,0.05)' }}>
                        <div className="text-2xl font-bold mb-2" style={{ color: theme.primaryColor }}>{prize.position}</div>
                        <div className="text-3xl font-bold text-white mb-4">{prize.amount}</div>
                        {prize.perks?.map((perk, i) => (
                          <div key={i} className="text-gray-400 text-sm">{perk}</div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          
          case 'faq':
            return (
              <div key={section.id} className="py-20 px-4">
                <div className="max-w-3xl mx-auto">
                  <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">{section.title}</h2>
                  <div className="space-y-4">
                    {section.questions?.map((faq, idx) => (
                      <div key={idx} className="p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)' }}>
                        <h3 className="text-lg font-semibold mb-2" style={{ color: theme.primaryColor }}>{faq.question}</h3>
                        <p className="text-gray-300">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          
          case 'custom':
            return (
              <div key={section.id} className="py-20 px-4">
                <div className="max-w-4xl mx-auto">
                  {section.title && <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">{section.title}</h2>}
                  <div className="prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: section.content }} />
                </div>
              </div>
            );
          
          default:
            return (
              <div key={section.id} className="py-20 px-4">
                <div className="max-w-4xl mx-auto text-center">
                  <h2 className="text-3xl font-bold">{section.title}</h2>
                </div>
              </div>
            );
        }
      })}
    </div>
  );
};

export default EnterpriseLandingPageBuilder;
